import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import {
  Button,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TooltipPopover,
} from 'src/core/ui';
import { useEventDiscoveryConfig } from 'src/hooks/api/useEventDiscoveryConfig';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import {
  EventDiscoveryConfigSettings,
  EventDiscoveryQuotaConfig,
  Feature,
  Marketplace,
  SyncCenterSettings,
} from 'src/WebApiController';

import * as styles from './EventDiscovery.css';

const useEventDiscoveryThresholds = (
  syncCenterQuery: SyncCenterSettings | null | undefined,
  eventDiscoveryConfigSettingsQuery:
    | EventDiscoveryConfigSettings
    | null
    | undefined
) => {
  const eventDiscoveryConfig = useMemo(
    () => eventDiscoveryConfigSettingsQuery?.eventDiscoveryConfig,
    [eventDiscoveryConfigSettingsQuery?.eventDiscoveryConfig]
  );

  const sellerAccountEventDiscoveryQuota = useMemo(
    () => eventDiscoveryConfigSettingsQuery?.sellerAccountEventDiscoveryQuota,
    [eventDiscoveryConfigSettingsQuery?.sellerAccountEventDiscoveryQuota]
  );

  const minEventDiscoveryProceedsAmount = useMemo(() => {
    const selectedConfig = (eventDiscoveryConfig?.quotaConfigs ?? []).find(
      ({ eventViewQuota }) =>
        eventViewQuota === sellerAccountEventDiscoveryQuota?.eventViewQuota
    );

    return selectedConfig?.stubHubProceedsAdvantageAmount ?? 0;
  }, [
    eventDiscoveryConfig?.quotaConfigs,
    sellerAccountEventDiscoveryQuota?.eventViewQuota,
  ]);

  const marketplaceMarkupsMeetMinThreshold = useMemo(
    () =>
      syncCenterQuery?.marketplaceSettings
        ?.filter(
          (mkt) => mkt.isEnabled && mkt.marketplace != Marketplace.StubHub
        )
        .every((mkt) => (mkt.markup ?? 0) >= minEventDiscoveryProceedsAmount) ??
      false,
    [syncCenterQuery?.marketplaceSettings, minEventDiscoveryProceedsAmount]
  );

  return {
    eventDiscoveryConfig,
    sellerAccountEventDiscoveryQuota,
    marketplaceMarkupsMeetMinThreshold,
  };
};

const getUpdateEventDiscoveryConfigInput = (
  eventDiscoveryQuotaConfig?: EventDiscoveryConfigSettings | null
): EventDiscoveryQuotaConfig => {
  const quotaConfigs =
    eventDiscoveryQuotaConfig?.eventDiscoveryConfig?.quotaConfigs ?? [];
  const sellerAccountEventDiscoQuota =
    eventDiscoveryQuotaConfig?.sellerAccountEventDiscoveryQuota?.eventViewQuota;

  const selectedConfig = quotaConfigs.find(
    ({ eventViewQuota }) => eventViewQuota === sellerAccountEventDiscoQuota
  );
  return {
    enabled:
      eventDiscoveryQuotaConfig?.sellerProceedsAdvantageConfig
        .hasActiveProceedsAdvantage ?? false,
    autoRenew:
      eventDiscoveryQuotaConfig?.sellerProceedsAdvantageConfig
        .hasRenewableActiveProceedsAdvantage ?? false,
    eventViewQuota:
      eventDiscoveryQuotaConfig?.sellerAccountEventDiscoveryQuota
        ?.eventViewQuota ?? 0,
    stubHubProceedsAdvantageAmount:
      selectedConfig?.stubHubProceedsAdvantageAmount ?? 0,
  };
};

export const EventDiscovery = () => {
  const {
    eventDiscoveryConfigSettingsQuery,
    eventDiscoveryConfigSettingsQueryIsLoading,
  } = useEventDiscoveryConfig();

  const syncCenterQuery = useSyncCenterSettings();

  const methods = useForm<EventDiscoveryQuotaConfig>({
    defaultValues: getUpdateEventDiscoveryConfigInput(
      eventDiscoveryConfigSettingsQuery
    ),
  });

  useEffect(() => {
    if (eventDiscoveryConfigSettingsQueryIsLoading) {
      return;
    }

    methods.reset(
      getUpdateEventDiscoveryConfigInput(eventDiscoveryConfigSettingsQuery)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventDiscoveryConfigSettingsQuery,
    eventDiscoveryConfigSettingsQueryIsLoading,
  ]);

  return eventDiscoveryConfigSettingsQueryIsLoading ||
    syncCenterQuery.isLoading ? null : (
    <FormProvider {...methods}>
      <EventDiscoveryContent />
    </FormProvider>
  );
};

export const EventDiscoveryContent = () => {
  const hasOnSaleEventDetailsViewFeature = useUserHasFeature(
    Feature.OnSaleEventDetailsView
  );

  const { isDarkMode } = useSiteTheme();

  const {
    eventDiscoveryConfigSettingsQuery,
    updateEventDiscoveryConfigMutation,
  } = useEventDiscoveryConfig();

  const syncCenterQuery = useSyncCenterSettings();

  const {
    eventDiscoveryConfig,
    sellerAccountEventDiscoveryQuota,
    marketplaceMarkupsMeetMinThreshold,
  } = useEventDiscoveryThresholds(
    syncCenterQuery.data,
    eventDiscoveryConfigSettingsQuery
  );

  const { formState, getValues, watch, setValue } =
    useFormContext<EventDiscoveryQuotaConfig>();

  const enabled = watch('enabled');
  const autoRenew = watch('autoRenew');
  const eventViewQuota = watch('eventViewQuota');

  const hasChanges = useCallback(
    (eventForm: EventDiscoveryQuotaConfig) => {
      return (
        formState.defaultValues?.enabled != eventForm.enabled ||
        formState.defaultValues?.eventViewQuota != eventForm.eventViewQuota
      );
    },
    [formState]
  );

  const onSubmitHandler = useCallback(() => {
    if (!hasChanges(getValues())) {
      return;
    }

    updateEventDiscoveryConfigMutation({
      input: getValues(),
    });
  }, [getValues, hasChanges, updateEventDiscoveryConfigMutation]);

  const proceedsAdvantageDurationInHours =
    eventDiscoveryConfig?.eventDiscoveryQuotaResetFrequencyInHours;

  return !hasOnSaleEventDetailsViewFeature ? null : (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.pageName}>
          <Content id={ContentId.StubhubDemandData} />
        </span>
        {marketplaceMarkupsMeetMinThreshold && (
          <Stack className={styles.markupMessage} alignItems="center" gap="m">
            <InfoOutlineIcon size={vars.iconSize.l} />
            <span>
              <Content id={ContentId.ExistingMarkupsMeetThreshold} />
            </span>
          </Stack>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.settingRow}>
          <div className={styles.settingText}>
            <Stack direction="row" gap="s" alignItems="center">
              <span className={styles.settingTitle}>
                <Content id={ContentId.ShowStubHubVisitsAndTxnData} />
              </span>
              <TooltipPopover
                triggerContent={
                  <InfoOutlineIcon withHoverEffect size={vars.iconSize.s} />
                }
                contentStyle={{
                  maxWidth: '300px',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                <Content id={ContentId.ShowStubHubVisitsAndTxnDataInfo} />
              </TooltipPopover>
            </Stack>
            <span className={styles.settingDescription}>
              <Content id={ContentId.ViewRealTimeInsights} />
            </span>
          </div>

          <Switch
            checked={enabled ?? false}
            onCheckedChange={(checked) => {
              setValue('enabled', checked);

              if (!checked) {
                setValue('autoRenew', checked);
              }
            }}
          />
        </div>

        <div className={styles.settingRow}>
          <div className={styles.settingText}>
            <span className={styles.settingTitle}>
              <Content id={ContentId.AutoRenew} />
            </span>
            <span className={styles.settingDescription}>
              <FormatContent
                id={FormatContentId.ViewAllocReset}
                params={[(proceedsAdvantageDurationInHours ?? 72) / 24 + '']}
              />
            </span>
          </div>

          <Switch
            checked={autoRenew ?? false}
            onCheckedChange={(checked) => {
              setValue('autoRenew', checked);
            }}
          />
        </div>

        <div className={styles.proceedsAdvantageRadioGroup}>
          <div className={styles.settingText}>
            <span className={styles.settingTitle}>
              <Content id={ContentId.SelectYourAccess} />
            </span>
          </div>
          <div>
            <Stack direction="column" gap="l" width="full">
              <RadioGroup
                onValueChange={(value: string) => {
                  const selectedConfig = (
                    eventDiscoveryConfig?.quotaConfigs ?? []
                  ).find(({ eventViewQuota }) => eventViewQuota + '' === value);
                  if (selectedConfig) {
                    setValue('eventViewQuota', selectedConfig.eventViewQuota);
                    setValue(
                      'stubHubProceedsAdvantageAmount',
                      selectedConfig.stubHubProceedsAdvantageAmount
                    );
                  }
                }}
                value={eventViewQuota?.toString()}
              >
                <Stack width="full" direction="column" gap="m">
                  {(eventDiscoveryConfig?.quotaConfigs ?? []).map(
                    (config, index) => (
                      <Stack
                        key={index}
                        width="full"
                        className={clsx(styles.UnlockMetricsAccessContainer, {
                          [styles.UnlockMetricsAccessContainerSelected]:
                            eventViewQuota === config.eventViewQuota,
                          [styles.actionableDark]:
                            eventViewQuota === config.eventViewQuota &&
                            isDarkMode,
                          [styles.radioOptionDisabled]:
                            sellerAccountEventDiscoveryQuota?.eventViewQuota !=
                              null &&
                            sellerAccountEventDiscoveryQuota.eventViewQuota ===
                              config.eventViewQuota,
                        })}
                        onClick={(e) => {
                          if (
                            sellerAccountEventDiscoveryQuota?.eventViewQuota ==
                            null
                          ) {
                            return;
                          }
                          e?.preventDefault();
                          e?.stopPropagation();
                          setValue('eventViewQuota', config.eventViewQuota);
                          setValue(
                            'stubHubProceedsAdvantageAmount',
                            config.stubHubProceedsAdvantageAmount
                          );
                        }}
                      >
                        <Radio value={JSON.stringify(config)} label={''} />
                        <Stack
                          width="full"
                          alignItems="center"
                          justifyContent="spaceBetween"
                          style={{ flex: 1 }}
                        >
                          <div>
                            <FormatContent
                              id={FormatContentId.EventViews}
                              params={[
                                applyNumberFormatting({
                                  precision: FormatOption.Whole,
                                  inputNumber: config.eventViewQuota,
                                }) as string,
                              ]}
                            />
                          </div>
                          <div>
                            <FormatContent
                              id={FormatContentId.OnSaleEventProceedsMarkup}
                              params={[
                                applyNumberFormatting({
                                  isPercent: true,
                                  inputNumber:
                                    config.stubHubProceedsAdvantageAmount ?? 0,
                                }) as string,
                              ]}
                            />
                          </div>
                        </Stack>
                      </Stack>
                    )
                  )}
                </Stack>
              </RadioGroup>
            </Stack>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.saveButton}>
          <Button
            className={clsx({
              [styles.actionableDark]: isDarkMode,
            })}
            disabled={!hasChanges(getValues())}
            onClick={() => {
              onSubmitHandler();
            }}
          >
            <Content id={ContentId.Confirm} />
          </Button>
        </div>
      </div>
    </div>
  );
};
